/**
 * The tasks tab (`KLQ-20260821-001`, C7/C8) — the shell the register and the board hang in.
 *
 * Three things live here and nowhere else, all because they belong to the *screen* rather than
 * to either list:
 *
 * 1. **The filter object.** It comes from the URL (`./filters.ts`) and is handed down. A
 *    control that narrows the register navigates; nothing on this screen keeps a private copy
 *    of what is being asked for, which is what makes a filtered register a link.
 * 2. **The version poll.** One per project, mounted here, so the board's four columns cannot
 *    become four polls (§E property 3). This is also the only place that knows which view is
 *    mounted, which is exactly what the invalidation rule needs — the poll is told the view
 *    that is actually on screen, so a change refetches what a person is looking at and marks
 *    the other view's cache stale without asking for it.
 * 3. **The view toggle.** Which of the three arrangements is `?view=` and nothing else —
 *    switching is a navigation that carries the whole filter object with it, so the board
 *    somebody narrowed is the register they get back, and the plan too
 *    (`KLQ-20260824-001` added the third).
 * 4. **The slicer rail.** Hoisted here from the hierarchy view on 2026-08-25, because *where*
 *    is a question about the screen and not about one arrangement of it. It sat inside
 *    `TaskHierarchy` while it was that view's own rail, and the register and the board asked
 *    the same question through a `<select>` in the filter bar — two controls writing one
 *    `under=`. The control is now one, above all three, and the bar's Location field is gone
 *    with the file that drew it. Somebody who slices to a floor and switches to the board
 *    keeps the slice, because the slice never belonged to the view.
 *
 *    **The rail slices by location and by nothing else** (owner, 2026-08-25). It briefly
 *    offered a second facet, *Assignee*, which wrote `filters.assignee` — the field the bar's
 *    own dropdown writes. That is not a wiring mistake but the shape of the idea: an attribute
 *    axis in the rail duplicates a bar control by construction, so clicking a person in the
 *    rail moved the dropdown, and *Clear filters* in the bar emptied the rail. The two are
 *    meant to be **independent controls over one intersected answer** — the bar narrows by
 *    what a task *is*, the rail by where it *sits*, each cleared on its own, both landing in
 *    the same request. A second person-predicate that intersected with the first was the other
 *    way out and was rejected: two assignee filters on one screen is a question nobody asks.
 *    This narrows `KLQ-20260820-005` §5's attribute axes; the note is in `KLQ-20260825-002`.
 * 5. **The generation entrance** (`KLQ-20260825-001`). It sits in the header rather than in
 *    one arrangement's toolbar, for the reason the rail is here: *raise the work on this part
 *    of the tree* is a question about the screen, and the thing it needs — the slice the rail
 *    is currently on — is held here. The drawer names no nodes; it forwards `filters.under`
 *    and the server intersects that with the caller's zone scope, so what the rail narrows is
 *    what the generation covers, with no second selection to keep in step.
 * 6. **The filter bar.** Hoisted here on 2026-09-10 (`KLQ-20260910-001` §A) for the reason the
 *    rail was: a filter is a question about the screen. It was drawn three times — once by the
 *    register, once by the plan, once by the board — with identical wiring, and all three
 *    inside `Screen`'s `children`, which is the one region on a screen that scrolls. So the
 *    controls scrolled away from the rows they act on, and on a laptop the bar was tall enough
 *    that no task row was visible on first paint. It goes in the `toolbar` slot, which is the
 *    one that never scrolls.
 */

import { useState } from "react";
import { Plus } from "lucide-react";

import { Button, Screen, ScreenHead, Slicer } from "@/components";
import { useMessages } from "@/lib/i18n";
import { CARDS, useMediaQuery } from "@/lib/media";

import { useTaskAxes } from "./axes";
import type { TaskFilters, TaskView } from "./filters";
import { TASK_VIEWS } from "./filters";
import { TaskBoard } from "./TaskBoard";
// UX cleanup 2026-08-25 — the generation entrance moved out of the header. See note 5.
// import { TaskGenerateDrawer } from "./TaskGenerateDrawer";
import { TaskCreateDrawer } from "./TaskCreateDrawer";
import { TaskHierarchy } from "./TaskHierarchy";
import { TaskRegister } from "./TaskRegister";
import { TaskFilterBar, useTaskFilterNavigation } from "./TaskFilterBar";
import styles from "./Tasks.module.css";
import { useTaskList, useTasksVersionPoll } from "./queries";

/** Which word names each arrangement in the toggle. */
const VIEW_KEY = {
  register: "view.register",
  board: "view.board",
  hierarchy: "view.hierarchy",
} as const;

export interface TasksPageProps {
  readonly projectId: string;
  /**
   * The URL's filter object, already parsed — the one object the register, the board and the
   * export read. Held here and handed down rather than re-derived per list (§B).
   */
  readonly filters: TaskFilters;
  readonly view: TaskView;
}

export function TasksPage(props: TasksPageProps) {
  const t = useMessages("tasks");
  // Mounting is opening (the Drawer's rule), so every press starts a blank form.
  // const [generating, setGenerating] = useState(false);
  // Hoisted from the register and the plan on 2026-08-25 (UX cleanup): *make a task* is a
  // question about the screen, not about the arrangement somebody happens to be reading it
  // in, and it was drawn twice — once per view — with identical wiring on both.
  const [creating, setCreating] = useState(false);
  // One writer per target view. Each closes over the view it navigates to, so the toggle is
  // three of them rather than a second opinion about what this screen's URL looks like.
  const toRegister = useTaskFilterNavigation(props.projectId, "register");
  const toBoard = useTaskFilterNavigation(props.projectId, "board");
  const toHierarchy = useTaskFilterNavigation(props.projectId, "hierarchy");
  const goTo: Readonly<Record<TaskView, (filters: TaskFilters) => void>> = {
    register: toRegister,
    board: toBoard,
    hierarchy: toHierarchy,
  };

  // The rail narrows by navigating, like every other control on this screen — and it navigates
  // to the view that is currently on screen, so slicing never also switches arrangement.
  const apply = goTo[props.view];

  // One poll for the whole screen. It is told what is mounted, so a change refetches that and
  // marks everything else stale without asking for it (§E).
  useTasksVersionPoll(props.projectId, props.view);

  /**
   * What the list on screen is holding, for the number on the active saved chip.
   *
   * **The same query the view below is already running, never a second one.** The register and
   * the plan both issue `taskListQueryOptions(projectId, filters, 'register')` verbatim, so
   * this is one more observer on a cache entry that already exists — no request of its own and
   * no key of its own. The board is the exception and reports nothing: its four columns are
   * four lists, and a number that spoke for one of them would be a count nobody asked for
   * (`KLQ-20260821-001` §D).
   *
   * **On the board the query is not asked at all** (`enabled`, review 2026-09-25 §3.21). It
   * used to run regardless and be thrown away four lines down — a whole register page in
   * flight on every board open, and re-marked stale by the 30 s poll for a number nobody
   * drew. Disabled, it is a cache entry nobody is filling, which is what the register finds
   * and fills the moment the view switches.
   */
  const list = useTaskList(
    props.projectId,
    props.filters,
    "register",
    props.view !== "board",
  );
  const loaded =
    props.view === "board" || !list.isSuccess
      ? null
      : {
          count: list.data.pages.reduce(
            (sum, page) => sum + page.items.length,
            0,
          ),
          hasMore: list.hasNextPage,
        };

  /**
   * **The rail's axes, read from the server** (`KLQ-20260820-005` §6).
   *
   * `GET /surfaces/project-tasks/descriptor` says what this endpoint filters on and what each
   * field accepts, and the dropdown is that answer. There is no list of axes on this screen:
   * an enum the server grows appears in the rail on the next deploy, and an attribute the
   * endpoint cannot filter on cannot be offered at all — which is the rule that keeps the
   * panel from becoming a second filtering mechanism beside the bar.
   *
   * Every value source it reads is already on this screen: the hierarchy is the Drawings tab's
   * cached tree, the members are the register's assignee column, the labels are its chips. The
   * rail costs this screen no request of its own beyond the descriptor, which is a release's
   * worth of cache.
   */
  const axes = useTaskAxes(props.projectId, props.filters);
  /**
   * Which axis the rail is on. Component state, like the arrangement and for the same reason
   * (`KLQ-20260820-006` §0.4): two people opening one shared link must see the same rows, and
   * which axis one of them has the rail set to is not part of the question the link asks.
   *
   * Held as an id that may have gone — the descriptor arrives a moment after the first render,
   * and a facet that is not there yet resolves to the rail's own first, which is `under`
   * whenever the surface has it.
   */
  const [facetId, setFacetId] = useState<string>("under");

  /**
   * **Where *Create task* is drawn** (owner, 2026-09-21: *"nech je to fakt menej toho tam na
   * telefone"*).
   *
   * One button, two places. Above 600px it is the screen head's action, where a writer looks
   * for it and where every other screen in the product keeps its Create. Under 600px the head
   * is already a title, a view toggle and this, stacked above a list that has about six card
   * rows of phone left — so it joins the toolbar row, beside the axis button and the filter
   * trigger, as the icon. `FilterBar`'s `actions` slot exists for exactly this: *what acts on
   * the result rather than on the question.*
   *
   * The word survives as the accessible name, so nothing is lost to a reader who cannot see
   * which glyph it is.
   */
  const phone = useMediaQuery(CARDS);
  const create = (
    <Button
      size="sm"
      variant="primary"
      icon={phone ? <Plus size={14} strokeWidth={1.5} aria-hidden /> : undefined}
      onClick={() => {
        setCreating(true);
      }}
    >
      {phone ? (
        <span className="sr-only">{t("create.action")}</span>
      ) : (
        t("create.action")
      )}
    </Button>
  );

  return (
    <>
      {/* `components/screen` (`KLQ-20260826-001`). The head and the rail hold still and the
          arrangement below scrolls: reaching task two hundred used to mean losing the filter
          rail and the *Create task* that acts on it. */}
      <Screen
        head={
          // Level 2: the tasks screen lives inside a project, whose name the shell's tab row
          // already carries as the document's `h1` (`STYLE.md` §6). No subtitle — see below.
          <ScreenHead
            level={2}
            title={t("page.title")}
            actions={
              <>
                <div
                  className={styles.viewToggle}
                  role="group"
                  aria-label={t("view.legend")}
                >
                  {TASK_VIEWS.map((view) => (
                    <button
                      key={view}
                      type="button"
                      aria-pressed={props.view === view}
                      className={[
                        styles.chip,
                        props.view === view ? styles.chipOn : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onClick={() => {
                        goTo[view](props.filters);
                      }}
                    >
                      {t(VIEW_KEY[view])}
                    </button>
                  ))}
                </div>
                {/* UX cleanup 2026-08-25 — the generation entrance. The mechanism is the best
                thing on this screen; `Generate` beside `Create task` was the worst way to
                offer it, because the two words say the same thing to a reader and only one
                of them is the ordinary case. It returns as a step of the project-shape
                wizard (run at project creation, re-enterable from project settings), which
                is where deciding the *shape* of the work belongs. `TaskGenerateDrawer` is
                untouched and waiting for it.
            <Button
              size="sm"
              onClick={() => {
                setGenerating(true);
              }}
            >
              {t("generate.action")}
            </Button>
            */}
                {/* The one Create on this screen. Both arrangements drew their own until today.
                    On a phone it is in the toolbar instead — see `create` above. */}
                {phone ? null : create}
              </>
            }
          />
        }
        toolbar={
          <TaskFilterBar
            projectId={props.projectId}
            filters={props.filters}
            view={props.view}
            loaded={loaded}
            actions={phone ? create : undefined}
          />
        }
        rail={
          <Slicer
            title={t("slicer.title")}
            facets={axes.facets}
            facetId={facetId}
            onFacetChange={setFacetId}
            sliced={axes.sliced(facetId)}
            onSlice={(ids) => {
              /**
               * **The rail writes one axis and touches no other.** Axes compose the way the
               * wire composes — OR within an axis, AND across them — so slicing to *In
               * progress* narrows what the location axis already narrowed, and each is cleared
               * on its own.
               *
               * **And the bar is the same state seen twice**, which is what this line buys:
               * the rail writes into `TaskFilters`, the bar reads out of it, and both are the
               * URL. Slicing to *In progress* shows as the status filter being set; clearing
               * it in either place clears it in both. Two controls over one predicate is fine;
               * two predicates that look like one is a bug report (§6).
               *
               * The whole next slice arrives from the rail, so holding *Block A and Block B* is
               * one navigation and not a merge performed here. The rail's ticks never arrive
               * here at all — they only become a slice when *Show only these* is pressed
               * (owner decision #86, 2026-09-15).
               */
              apply(axes.apply(props.filters, facetId, ids));
            }}
          />
        }
      >
        <div className={styles.railMain}>
          {props.view === "board" ? (
            <TaskBoard projectId={props.projectId} filters={props.filters} />
          ) : props.view === "hierarchy" ? (
            <TaskHierarchy
              projectId={props.projectId}
              filters={props.filters}
            />
          ) : (
            <TaskRegister projectId={props.projectId} filters={props.filters} />
          )}
        </div>
      </Screen>

      {/* The slice the rail is on, handed over verbatim — never a list of node ids this
          screen computed.
      {generating ? (
        <TaskGenerateDrawer
          projectId={props.projectId}
          under={props.filters.under}
          onClose={() => {
            setGenerating(false);
          }}
        />
      ) : null}
      */}

      {/* The slice the rail is on is the drawer's pre-filled location, exactly as it was when
          each view mounted its own copy of this. */}
      {creating ? (
        <TaskCreateDrawer
          projectId={props.projectId}
          locationId={props.filters.under[0] ?? null}
          onClose={() => {
            setCreating(false);
          }}
        />
      ) : null}
    </>
  );
}
